/**
 * Validation of untrusted wire input (SPEC §14). Builds fresh objects from known fields only, so
 * unexpected keys, prototype tricks or oversized values never reach the graph or the database.
 */
import { MAX_CONTENT, MAX_TITLE, makeNode, type Node, type NodeKind, type Origin } from './model.ts'
import type { OpBody } from './ops.ts'
import type { PushRequest, WireOp, WireTxn } from './protocol.ts'
import { normalizeTitle, titleProblem } from './titles.ts'

export class InvalidInput extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidInput'
  }
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/
const ORDER = /^[0-9A-Za-z]{1,200}$/
const DATE = /^\d{4}-\d{2}-\d{2}$/
const KINDS: ReadonlySet<NodeKind> = new Set(['block', 'page', 'journal', 'tag', 'asset', 'view'])
const ORIGINS: ReadonlySet<Origin> = new Set(['user', 'ai', 'import', 'calendar'])
export const LIMITS = {
  title: MAX_TITLE,
  tags: 100,
  propBytes: 64 * 1024,
  opsPerTxn: 10_000,
  txnsPerPush: 500,
  clientId: 64,
} as const

export const isUuid = (x: unknown): x is string => typeof x === 'string' && UUID.test(x)

const fail = (m: string): never => {
  throw new InvalidInput(m)
}
const obj = (x: unknown, what: string): Record<string, unknown> =>
  x !== null && typeof x === 'object' && !Array.isArray(x) ? (x as Record<string, unknown>) : fail(`${what}: object expected`)
const uuid = (x: unknown, what: string): string => (isUuid(x) ? x : fail(`${what}: uuid expected`))
const str = (x: unknown, max: number, what: string): string =>
  typeof x === 'string' && x.length <= max ? x : fail(`${what}: string ≤ ${max} expected`)
const bool = (x: unknown, what: string): boolean => (typeof x === 'boolean' ? x : fail(`${what}: boolean expected`))
/** A title as stored: already normalised, and usable as `[[Title]]` (D4). */
const title = (x: unknown, what: string): string => {
  const t = str(x, LIMITS.title, what)
  if (normalizeTitle(t) !== t) fail(`${what}: not normalised`)
  const problem = titleProblem(t)
  if (problem) fail(`${what}: ${problem}`)
  return t
}
const num = (x: unknown, what: string): number =>
  typeof x === 'number' && Number.isFinite(x) ? x : fail(`${what}: number expected`)

/** A JSON value of bounded size, re-parsed so only plain data survives. */
function jsonValue(x: unknown, what: string): unknown {
  const s = JSON.stringify(x ?? null)
  if (s === undefined || s.length > LIMITS.propBytes) fail(`${what}: JSON ≤ ${LIMITS.propBytes} bytes expected`)
  return JSON.parse(s)
}

function node(x: unknown): Node {
  const n = obj(x, 'node')
  const kind = n.kind
  if (typeof kind !== 'string' || !KINDS.has(kind as NodeKind)) fail('node.kind')
  const origin = n.origin ?? 'user'
  if (typeof origin !== 'string' || !ORIGINS.has(origin as Origin)) fail('node.origin')
  if (n.deletedAt !== null && n.deletedAt !== undefined) fail('node.deletedAt must be null')
  const tags = Array.isArray(n.tags) ? n.tags : fail('node.tags')
  if (tags.length > LIMITS.tags) fail('node.tags: too many')
  const props = jsonValue(n.props ?? {}, 'node.props')
  if (props === null || typeof props !== 'object' || Array.isArray(props)) fail('node.props: object expected')
  const date = n.date ?? null
  if (date !== null && !(typeof date === 'string' && DATE.test(date))) fail('node.date')
  const order = str(n.order, 200, 'node.order')
  if (!ORDER.test(order)) fail('node.order: base-62 key expected')
  return makeNode({
    id: uuid(n.id, 'node.id'),
    kind: kind as NodeKind,
    content: str(n.content ?? '', MAX_CONTENT, 'node.content'),
    title: n.title === null || n.title === undefined ? null : title(n.title, 'node.title'),
    parentId: n.parentId === null || n.parentId === undefined ? null : uuid(n.parentId, 'node.parentId'),
    order,
    tags: tags.map((t, i) => uuid(t, `node.tags[${i}]`)),
    props: props as Record<string, unknown>,
    date: date as string | null,
    collapsed: bool(n.collapsed ?? false, 'node.collapsed'),
    private: bool(n.private ?? false, 'node.private'),
    origin: origin as Origin,
    createdAt: num(n.createdAt ?? 0, 'node.createdAt'),
    updatedAt: num(n.updatedAt ?? 0, 'node.updatedAt'),
    deletedAt: null,
  })
}

export function parseOpBody(x: unknown): OpBody {
  const o = obj(x, 'op')
  const nodeId = uuid(o.nodeId, 'op.nodeId')
  const p = obj(o.payload ?? {}, 'op.payload')
  switch (o.type) {
    case 'node.create': {
      const n = node(p.node)
      if (n.id !== nodeId) fail('node.create: id mismatch')
      return { type: 'node.create', nodeId, payload: { node: n } }
    }
    case 'node.content':
      return { type: 'node.content', nodeId, payload: { content: str(p.content, MAX_CONTENT, 'content') } }
    case 'node.move': {
      const order = str(p.order, 200, 'order')
      if (!ORDER.test(order)) fail('order: base-62 key expected')
      return { type: 'node.move', nodeId, payload: { parentId: uuid(p.parentId, 'parentId'), order } }
    }
    case 'node.collapse':
      return { type: 'node.collapse', nodeId, payload: { collapsed: bool(p.collapsed, 'collapsed') } }
    case 'node.title':
      return {
        type: 'node.title',
        nodeId,
        payload: { title: p.title === null ? null : title(p.title, 'title') },
      }
    case 'node.relink':
      return {
        type: 'node.relink',
        nodeId,
        payload: { from: str(p.from, LIMITS.title, 'from'), to: title(p.to, 'to') },
      }
    case 'node.props':
      return {
        type: 'node.props',
        nodeId,
        payload: { field: str(p.field, 100, 'field'), value: jsonValue(p.value, 'value') },
      }
    case 'node.tags': {
      const tags = Array.isArray(p.tags) ? p.tags : fail('tags')
      if (tags.length > LIMITS.tags) fail('tags: too many')
      return { type: 'node.tags', nodeId, payload: { tags: tags.map((t, i) => uuid(t, `tags[${i}]`)) } }
    }
    case 'node.delete':
      return { type: 'node.delete', nodeId, payload: {} }
    case 'node.restore': {
      const ids = Array.isArray(p.ids) ? p.ids : fail('ids')
      if (ids.length > 100_000) fail('ids: too many')
      return { type: 'node.restore', nodeId, payload: { ids: ids.map((t, i) => uuid(t, `ids[${i}]`)) } }
    }
    default:
      return fail(`unknown op type ${String(o.type)}`)
  }
}

function wireOp(x: unknown): WireOp {
  const o = obj(x, 'wire op')
  const clientSeq = num(o.clientSeq, 'clientSeq')
  if (!Number.isInteger(clientSeq) || clientSeq < 1) fail('clientSeq: positive integer expected')
  return { id: uuid(o.id, 'op.id'), clientSeq, ts: num(o.ts, 'ts'), body: parseOpBody(o.body) }
}

function wireTxn(x: unknown): WireTxn {
  const t = obj(x, 'txn')
  const ops = Array.isArray(t.ops) ? t.ops : fail('txn.ops')
  if (ops.length === 0 || ops.length > LIMITS.opsPerTxn) fail(`txn.ops: 1–${LIMITS.opsPerTxn} expected`)
  return { id: uuid(t.id, 'txn.id'), ops: ops.map(wireOp) }
}

export function parsePushRequest(x: unknown): PushRequest {
  const r = obj(x, 'push')
  const clientId = str(r.clientId, LIMITS.clientId, 'clientId')
  if (!/^[A-Za-z0-9._-]+$/.test(clientId)) fail('clientId: [A-Za-z0-9._-] expected')
  const txns = Array.isArray(r.txns) ? r.txns : fail('txns')
  if (txns.length > LIMITS.txnsPerPush) fail(`txns: ≤ ${LIMITS.txnsPerPush} expected`)
  return { clientId, txns: txns.map(wireTxn) }
}
